
import { defineComponent } from "vue";
import { setCurrentPageTitle } from "@/core/helpers/breadcrumb";
import ApiService from "@/core/services/ApiService";
import { ElNotification } from "element-plus";

type RegistryColumn = {
  label: string;
  prop: string;
  width?: number;
  link?: boolean;
  money?: boolean;
};

const configs = {
  customers: {
    kind: "customers",
    title: "Зээлдэгчид",
    kicker: "T_CUSTOMER, T_CUSTOMER_DETAILS",
    description:
      "Зээлдэгчийн үндсэн болон дэлгэрэнгүй мэдээллийг хурдан шүүх, шалгах лавлагаа.",
    endpoint: "reference/customers",
    roles: ["ROLE_CUSTOMER_LIST"],
    columns: [
      { label: "Зээлдэгчийн дугаар", prop: "identityNo", width: 160 },
      { label: "Регистр", prop: "stateRegisterNo", width: 150 },
      { label: "Нэр", prop: "customerName", width: 220 },
      { label: "Овог", prop: "lastName", width: 160 },
      { label: "Төрөл", prop: "customerType", width: 140 },
      { label: "Утас", prop: "phone", width: 130 },
      { label: "И-мэйл", prop: "email", width: 180 },
      { label: "Сүүлд шинэчлэгдсэн", prop: "lastUpdateDate", width: 160 }
    ] as RegistryColumn[]
  },
  relations: {
    kind: "relations",
    title: "Холбогдох этгээд",
    kicker: "T_CUSTOMER_RELATIONS",
    description:
      "Зээлтэй холбоотой иргэн, байгууллага, хувьцаа эзэмшигчийн мэдээллийг хайх лавлагаа.",
    endpoint: "reference/customer-relations",
    roles: ["ROLE_CUSTOMER_RELATIONS_LIST"],
    columns: [
      {
        label: "Зээлийн гэрээний дугаар",
        prop: "contractNo",
        width: 180,
        link: true
      },
      { label: "Зээлийн дугаар", prop: "loanCode", width: 150 },
      { label: "Зээлдэгч", prop: "customerName", width: 220 },
      { label: "Зээлдэгчийн дугаар", prop: "identityNo", width: 160 },
      { label: "Холбогдох этгээд", prop: "relationName", width: 220 },
      { label: "Регистр", prop: "relationRegisterNo", width: 150 },
      { label: "Хамаарал", prop: "relationTypeName", width: 180 },
      { label: "Сүүлд шинэчлэгдсэн", prop: "lastUpdateDate", width: 160 }
    ] as RegistryColumn[]
  },
  collaterals: {
    kind: "collaterals",
    title: "Барьцаа хөрөнгө",
    kicker: "T_COLLATERAL, T_COLLATERAL_LINE",
    description:
      "Зээл болон зээлийн шугамын барьцаа, эзэмшигчийн мэдээллийг нэг дороос харах лавлагаа.",
    endpoint: "reference/collaterals",
    roles: ["ROLE_COLLATERAL_LINE_LIST", "ROLE_COLLATERAL_LIST"],
    columns: [
      { label: "Гэрээний дугаар", prop: "contractNo", width: 180, link: true },
      { label: "Зээл/шугамын дугаар", prop: "loanCode", width: 160 },
      { label: "Барьцааны дугаар", prop: "collCode", width: 160 },
      { label: "Барьцааны төрөл", prop: "collateralTypeName", width: 220 },
      { label: "Бүртгэлийн дугаар", prop: "collateralRegisterNo", width: 170 },
      { label: "Үнэлгээ", prop: "value", width: 150, money: true },
      { label: "Эзэмшигч", prop: "ownerName", width: 220 },
      { label: "Хаяг", prop: "address", width: 260 }
    ] as RegistryColumn[]
  }
};

export default defineComponent({
  name: "ReferenceRegistry",
  data() {
    return {
      page: 1,
      pageSize: 20,
      total: 0,
      rows: [] as Array<Record<string, any>>,
      search: "",
      customerType: "",
      loading: false,
      collateralType: "loan"
    };
  },
  computed: {
    config(): any {
      const key = (this.$route.meta.registry as string) || "customers";
      return configs[key] || configs.customers;
    },
    visibleColumns(): RegistryColumn[] {
      return this.config.columns;
    },
    canAccess(): boolean {
      return this.$isMbUser && this.hasAnyConfiguredRole(this.config.roles);
    },
    currentRange(): string {
      if (!this.total) {
        return "0";
      }
      const start = (this.page - 1) * this.pageSize + 1;
      const end = Math.min(this.page * this.pageSize, this.total);
      return `${this.formatNumber(start)}-${this.formatNumber(end)}`;
    }
  },
  watch: {
    "$route.meta.registry": {
      handler() {
        this.page = 1;
        this.search = "";
        this.customerType = "";
        this.prepareCollateralType();
        setCurrentPageTitle(this.config.title);
        if (this.canAccess) {
          this.fetchRows();
        }
      }
    }
  },
  mounted() {
    this.prepareCollateralType();
    setCurrentPageTitle(this.config.title);
    if (this.canAccess) {
      this.fetchRows();
    }
  },
  methods: {
    prepareCollateralType() {
      if (this.config.kind !== "collaterals") {
        return;
      }
      const hasLoan = this.$hasAnyRole("T_COLLATERAL", "T_COLLATERAL_OWNERS");
      const hasLine = this.$hasAnyRole(
        "T_COLLATERAL_LINE",
        "T_COLLATERAL_LINE_OWNERS"
      );
      if (!hasLoan && hasLine) {
        this.collateralType = "loanline";
      } else {
        this.collateralType = "loan";
      }
    },
    hasAnyConfiguredRole(roles: string[]): boolean {
      return roles.some(role => this.$hasRole(role));
    },
    indexMethod(index: number): number {
      return (this.page - 1) * this.pageSize + index + 1;
    },
    submitSearch() {
      this.page = 1;
      this.fetchRows();
    },
    handleCurrentChange(page: number) {
      this.page = page;
      this.fetchRows();
    },
    handleSizeChange() {
      this.page = 1;
      this.fetchRows();
    },
    handleCollateralTypeChange() {
      this.page = 1;
      this.fetchRows();
    },
    handleCustomerTypeChange() {
      this.page = 1;
      this.fetchRows();
    },
    normalizeRows(payload: any): Array<Record<string, any>> {
      return (
        payload?.items ||
        payload?.rows ||
        payload?.content ||
        payload?.result ||
        payload?.data ||
        []
      );
    },
    normalizeTotal(payload: any, rows: Array<Record<string, any>>): number {
      return Number(
        payload?.totalElements ??
          payload?.total ??
          payload?.count ??
          rows.length ??
          0
      );
    },
    async fetchRows() {
      if (!this.canAccess) {
        return;
      }
      this.loading = true;
      try {
        const response = await ApiService.query(this.config.endpoint, {
          params: {
            page: this.page,
            size: this.pageSize,
            search: this.search,
            customerType:
              this.config.kind === "customers" ? this.customerType : undefined,
            type:
              this.config.kind === "collaterals"
                ? this.collateralType
                : undefined
          }
        });
        const result = response.data;
        if (result.code === 0 || result.code === 401) {
          ElNotification({
            title: result.code === 401 ? "Анхааруулга" : "Алдаа",
            message:
              result.warning || result.error || "Өгөгдөл татахад алдаа гарлаа.",
            type: result.code === 401 ? "warning" : "error"
          });
          this.rows = [];
          this.total = 0;
          return;
        }
        const payload = result.response || result;
        const rows = this.normalizeRows(payload);
        this.rows = rows;
        this.total = this.normalizeTotal(payload, rows);
      } catch (error) {
        ElNotification({
          title: "Алдаа",
          message: "Өгөгдөл татахад алдаа гарлаа.",
          type: "error"
        });
      } finally {
        this.loading = false;
      }
    },
    getLoanNavigation(row: Record<string, any>) {
      const type =
        row.detailType ||
        row.type ||
        (this.collateralType === "loanline" ? "loanline" : "loan");
      const loanCode =
        type === "loanline"
          ? row.loanlineCode || row.loanLineCode || row.loanCode
          : row.loanCode || row.loancode;
      const orgCode = row.providerRegnum || row.orgCode || row.orgcode || "";
      const customerCode =
        row.identityNo ||
        row.identityno ||
        row.customerCode ||
        row.customercode ||
        "";

      if (!loanCode || !customerCode) {
        return null;
      }

      return {
        loanCode,
        orgCode,
        customerCode,
        type
      };
    },
    openLoanDetails(row: Record<string, any>) {
      const navigation = this.getLoanNavigation(row);
      if (!navigation) {
        return;
      }
      const id = (this as any).b64EncodeUnicode(
        `${navigation.loanCode}@${navigation.orgCode}@${navigation.customerCode}@${navigation.type}`
      );
      this.$router.push({
        name: "refloandetails",
        params: { id }
      });
    },
    formatMoney(value: any): string {
      if (value === null || value === undefined || value === "") {
        return "-";
      }
      return new Intl.NumberFormat("mn-MN", {
        maximumFractionDigits: 2
      }).format(Number(value));
    },
    formatNumber(value: any): string {
      return new Intl.NumberFormat("mn-MN").format(Number(value || 0));
    }
  }
});
